import {Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Blog} from "./types/blog";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {BlogPost} from 'src/app/blog/types/blog-post';
import {BlogService} from 'src/app/blog/blog.service';
import {environment} from "../../../../environments/environment";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {WebpImageComponent} from "../../../components/webp-image/webp-image.component";

@Component({
  selector: 'app-widget-blog',
  standalone: true,
  imports: [CommonModule, RouterLink, WebpImageComponent],
  templateUrl: './blog.component.html',
  styleUrls: ['./blog.component.less']
})
export class BlogComponent implements OnInit {
  @Input() widget!: Blog;
  public blogPosts: BlogPost[] = [];
  public currentPage: number = 1;
  public totalPages: number = 1;
  public totalPosts: number = 0;
  public pageSize: number = 10;

  public constructor(
    private blogService: BlogService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    // Check if pagination is enabled
    if (this.isPaginationEnabled()) {
      // Get page from query params
      this.route.queryParams.subscribe(params => {
        this.currentPage = params['page'] ? parseInt(params['page'], 10) : 1;
        this.loadBlogPosts();
      });
    } else {
      // Load all posts without pagination
      this.loadBlogPosts();
    }
  }

  private isPaginationEnabled(): boolean {
    return !!(this.widget.data.postsPerPage && this.widget.data.postsPerPage > 0);
  }

  private loadBlogPosts(): void {
    const paginationEnabled = this.isPaginationEnabled();
    const postsPerPage = this.widget.data.postsPerPage || this.pageSize;

    // Use postsPerPage for pagination, or limit for total results
    const limit = paginationEnabled ? postsPerPage : (this.widget.data.limit || null);
    const offset = paginationEnabled ? (this.currentPage - 1) * postsPerPage : null;

    this.blogService.getBlogPosts(
      this.widget.data.filters?.categories,
      limit ?? 0,
      undefined,
      undefined,
      offset ?? undefined,
    ).subscribe(
      response => {
        this.blogPosts = response.items;
        this.totalPosts = response.total;
        this.pageSize = postsPerPage;
        this.totalPages = paginationEnabled ? Math.ceil(this.totalPosts / this.pageSize) : 1;
      }
    );
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: page },
      queryParamsHandling: 'merge'
    });
  }

  public get pages(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  public excerpt(article: BlogPost, limit: number = 450): SafeHtml {
    if (!article.text) return '';

    const div = document.createElement('div');
    div.innerHTML = article.text;

    let charCount = 0;
    const truncateNodes = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        charCount += node.textContent?.length ?? 0;
        if (charCount > limit) {
          node.textContent = node.textContent?.substring(0, node.textContent.length - (charCount - limit)) + '...';
          return true;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        for (let i = 0; i < node.childNodes.length; i++) {
          if (truncateNodes(node.childNodes[i])) {
            while (node.childNodes.length > i + 1) {
              node.removeChild(node.childNodes[i + 1]);
            }
            return true;
          }
        }
      }
      return false;
    };

    truncateNodes(div);

    return this.sanitizer.bypassSecurityTrustHtml(div.innerHTML);
  }

  public getImageUrl(article: BlogPost): string {
    return `${environment.cdn.url}/blogs/thumbnails/medium/${article.image}`;
  }
}
